import React, { useState, useEffect, Suspense, lazy } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Alert,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

import { useTranslation } from "react-i18next";

import {
  uploadFile,
  getFiles,
  downloadFile,
  deleteSpecificFile,
} from "../../api/Design/fileApi"; // Ajustar paths según tu proyecto
import {
  updateMuestra,
  getMuestraById,
  getMuestrasProveedores,
  /* getProductMuestras, */
  getMuestrasTipo,
  getEscandallosByMuestraId,
  getEscandalloGrupos,
  getEscandalloSubgruposMuestras,
  getMuestrasEstado,
  getMuestrasTallas,
  getMuestrasColores,
  moveTechnicalFileBetweenMuestras,
  moveDesignTechnicalToMuestra,
} from "../../api/Muestras/muestraApi";

import { getMarcas } from "../../api/Marcas/marcaApi";

import dayjs from "dayjs";
/* import DrawerMuestra from "../../components/Muestras/DrawerMuestra"; */
import DrawerMuestraV2 from "../../components/Muestras/Drawer/DrawerMuestraV2";
import { parseMuestraDate } from "../../utils/dateUtils";

import FileThumbnails from "../Estimates/FileThumbnails";
import TechnicalAttachmentsPanel, {
  TECH_DRAG_MIME,
  pastillaBaseSx,
} from "./TechnicalAttachmentsPanel";
import { getMuestraFileCategory } from "../../utils/muestraFileCategories";

const SampleBoardDialog = lazy(() => import("./SampleBoardDialog"));

const FileUploader = ({
  productId,
  productName,
  temporada,
  marca,
  designer,
  muestras,
  onDeleteMuestra,
  onRefresh,
  confirmDelete,
  onMuestraUpdated,
  userCanCreateSample,
  userCanMoodboard = false,
  onFilesStatusChange,
  selectedSampleId: selectedSampleIdProp = undefined,
  onSelectSample,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();

  // 🟢 NUEVO: estados para drag & drop
  const [dragOverFolder, setDragOverFolder] = useState(null);

  const [files, setFiles] = useState({
    Muestras: [],
  });
  const [hasTechnicalFiles, setHasTechnicalFiles] = useState(false);
  const [selectedSampleIdInternal, setSelectedSampleIdInternal] =
    useState(null);
  const isSampleControlled = selectedSampleIdProp !== undefined;
  const selectedSampleId = isSampleControlled
    ? selectedSampleIdProp
    : selectedSampleIdInternal;
  const setSelectedSampleId = (id) => {
    if (!isSampleControlled) setSelectedSampleIdInternal(id);
    onSelectSample?.(id);
  };
  const [techRefreshKey, setTechRefreshKey] = useState(0);
  const [boardMuestraId, setBoardMuestraId] = useState(null);
  const [dragOverTechTargetId, setDragOverTechTargetId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [muestraDrawerOpen, setMuestraDrawerOpen] = useState(false);
  const [selectedMuestra, setSelectedMuestra] = useState(null);
  const [tiposMuestra, setTiposMuestra] = useState([]);
  const [tiposProveedores, setTiposProveedores] = useState([]);
  const [tiposEstado, setTiposEstado] = useState([]);
  const [tiposTallas, setTiposTallas] = useState([]);
  const [tiposColores, setTiposColores] = useState([]);
  const [escandalloGrupos, setEscandalloGrupos] = useState([]);
  const [escandalloSubgrupos, setEscandalloSubgrupos] = useState([]);

  const [marcas, setMarcas] = useState([]);

  const firstClientName =
    muestras?.find((m) => m?.NombreCliente)?.NombreCliente || "";

  const selectedSample =
    muestras?.find((m) => Number(m.ID_MUESTRA) === Number(selectedSampleId)) ||
    null;

  useEffect(() => {
    if (!muestras?.length) {
      setSelectedSampleId(null);
      return;
    }
    const stillExists = muestras.some(
      (m) => Number(m.ID_MUESTRA) === Number(selectedSampleId),
    );
    if (!stillExists) {
      setSelectedSampleId(muestras[0].ID_MUESTRA);
    }
  }, [muestras, selectedSampleId]);

  const sanitizeBaseName = (value = "") =>
    value
      .toString()
      .trim()
      .replace(/[\\/:*?"<>|]/g, "-")
      .replace(/\s+/g, " ")
      .replace(/\.+$/, "");

  const getFileExtension = (fileName = "") => {
    const lastDot = fileName.lastIndexOf(".");
    return lastDot >= 0 ? fileName.slice(lastDot) : "";
  };

  const escapeRegex = (value = "") =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const buildRenamedFile = (file, baseName, existingFiles = []) => {
    if (!file) return null;

    const safeBaseName = sanitizeBaseName(baseName) || "archivo";
    const regex = new RegExp(`^${escapeRegex(safeBaseName)}-(\\d+)`, "i");

    const maxCounter = existingFiles.reduce((max, existingFile) => {
      const match = existingFile?.name?.match(regex);
      if (!match) return max;
      const num = parseInt(match[1], 10);
      return Number.isNaN(num) ? max : Math.max(max, num);
    }, 0);

    const extension = getFileExtension(file.name);
    const nextCounter = String(maxCounter + 1).padStart(2, "0");
    const newName = `${safeBaseName}-${nextCounter}${extension}`;

    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const getMuestraFiles = (muestra) =>
    files["Muestras"].filter((file) => {
      // Only root sample photos (exclude Calidades/Print/tech subfolders)
      if (getMuestraFileCategory(file)) return false;

      const normalizedPath = file.path.replaceAll("\\", "/").toLowerCase();
      const id = muestra.ID_MUESTRA.toString();
      const sampleName = (muestra.MUESTRA || "").toLowerCase();

      const belongsToSample =
        normalizedPath.includes(`/muestras/${id}/`) ||
        normalizedPath.includes(`/muestras/${id}-`) ||
        (sampleName && normalizedPath.includes(`/muestras/${sampleName}/`));

      if (!belongsToSample) return false;

      const marker = sampleName
        ? `/muestras/${sampleName}/`
        : `/muestras/${id}/`;
      const idx = normalizedPath.indexOf(marker);
      if (idx === -1) return false;
      const rest = normalizedPath.slice(idx + marker.length);
      return Boolean(rest) && !rest.includes("/");
    });

  const getSampleUploadBaseName = (muestra) => {
    const clientName = sanitizeBaseName(muestra?.NombreCliente || "");
    const sampleName = sanitizeBaseName(muestra?.MUESTRA || "");
    if (clientName && sampleName) return `${clientName}-${sampleName}`;
    return clientName || sampleName || "muestra";
  };

  const handleCloseDrawer = () => {
    setMuestraDrawerOpen(false);
    setSelectedMuestra(null);

    loadFiles("Muestras");
  };

  // 3. Cargar archivos por carpeta
  const loadFiles = async (folder) => {
    if (!productName || !temporada || !marca || !designer || !folder) return;

    setLoading(true);
    try {
      const fetchedFiles = await getFiles(
        temporada,
        marca,
        designer,
        productName,
        folder,
      );
      setFiles((prevFiles) => ({
        ...prevFiles,
        [folder]: fetchedFiles,
      }));
    } catch (error) {
      /*  setErrorMessage("Error al cargar los archivos."); */
    }
    setLoading(false);
  };

  // Cada vez que cambie productId / productName, recargamos las carpetas
  useEffect(() => {
    if (productId && productName) {
      loadFiles("Muestras");
    }
  }, [productId, productName]);

  /* fetchEstados */
  useEffect(() => {
    const fetchEstados = async () => {
      try {
        const estados = await getMuestrasEstado();
        setTiposEstado(estados);
      } catch (error) {
        console.error("Error al obtener estados de muestra:", error);
      }
    };
    fetchEstados();
  }, []);

  /* fetchTiposTallas */
  useEffect(() => {
    const fetchTiposTallas = async () => {
      try {
        const tipos = await getMuestrasTallas();
        setTiposTallas(tipos);
      } catch (error) {
        console.error("Error al obtener tallas de muestra:", error);
      }
    };
    fetchTiposTallas();
  }, []);

  /* fetchTiposColores */
  useEffect(() => {
    const fetchTiposColores = async () => {
      try {
        const tipos = await getMuestrasColores();
        setTiposColores(tipos);
      } catch (error) {
        console.error("Error al obtener colores de muestra:", error);
      }
    };
    fetchTiposColores();
  }, []);

  /* fetchProveedores */
  const fetchProveedores = async () => {
    try {
      const proveedores = await getMuestrasProveedores();
      setTiposProveedores(proveedores);
    } catch (error) {
      console.error("Error fetching proveedores:", error);
    }
  };

  useEffect(() => {
    fetchProveedores();
  }, []);

  /* fetchEscandalloGrupos */
  useEffect(() => {
    const fetchEscandalloGrupos = async () => {
      try {
        const grupos = await getEscandalloGrupos();
        setEscandalloGrupos(grupos);
      } catch (error) {
        console.error("Error al obtener grupos de escandallo:", error);
      }
    };
    fetchEscandalloGrupos();
  }, []);

  // 5. Subgrupos en función del grupo
  const handleGrupoChange = async (event) => {
    const grupoId = event.target.value;
    try {
      const subgrupos = await getEscandalloSubgruposMuestras(grupoId);
      setEscandalloSubgrupos(subgrupos);
      setSelectedMuestra((prev) => ({
        ...prev,
        ESCANDALLO_GRUPOS: grupoId,
        ESCANDALLO_SUBGRUPO: "",
      }));
    } catch (error) {
      console.error("Error al obtener subgrupos de escandallo:", error);
    }
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
    setDragOverTechTargetId(null);
  };

  const parseTechDragPayload = (dataTransfer) => {
    try {
      const raw =
        dataTransfer.getData(TECH_DRAG_MIME) ||
        dataTransfer.getData("text/plain");
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (!payload?.folder || !payload?.fileName) return null;
      if (payload.source !== "design" && payload.source !== "muestra") {
        // Back-compat: old payload only had sourceMuestraId
        if (payload.sourceMuestraId) {
          return { ...payload, source: "muestra" };
        }
        return null;
      }
      return payload;
    } catch {
      return null;
    }
  };

  const handleMoveTechnicalToSample = async (targetMuestra, dataTransfer) => {
    const payload = parseTechDragPayload(dataTransfer);
    if (!payload || !targetMuestra?.ID_MUESTRA) return false;

    try {
      if (payload.source === "design") {
        await moveDesignTechnicalToMuestra(targetMuestra.ID_MUESTRA, {
          folder: payload.folder,
          fileName: payload.fileName,
        });
        setSelectedSampleId(targetMuestra.ID_MUESTRA);
        setTechRefreshKey((k) => k + 1);
        return true;
      }

      if (
        payload.source === "muestra" &&
        payload.sourceMuestraId &&
        Number(payload.sourceMuestraId) !== Number(targetMuestra.ID_MUESTRA)
      ) {
        await moveTechnicalFileBetweenMuestras(payload.sourceMuestraId, {
          targetMuestraId: targetMuestra.ID_MUESTRA,
          folder: payload.folder,
          fileName: payload.fileName,
        });
        setSelectedSampleId(targetMuestra.ID_MUESTRA);
        setTechRefreshKey((k) => k + 1);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error moviendo adjunto técnico:", error);
      return false;
    }
  };

  const isImageFile = (file) => {
    if (!file) return false;
    return file.type?.startsWith("image/");
  };

  const uploadMuestraImage = async (fileOrFiles, muestra) => {
    const selectedFiles = Array.isArray(fileOrFiles)
      ? fileOrFiles
      : Array.from(fileOrFiles || []);
    const imageFiles = selectedFiles.filter((file) => isImageFile(file));

    if (
      imageFiles.length === 0 ||
      !temporada ||
      !marca ||
      !designer ||
      !productName ||
      !muestra?.ID_MUESTRA
    ) {
      return;
    }

    try {
      let existingMuestraFiles = getMuestraFiles(muestra).slice();
      const sampleBaseName = getSampleUploadBaseName(muestra);
      for (const file of imageFiles) {
        const renamedFile = buildRenamedFile(
          file,
          sampleBaseName,
          existingMuestraFiles,
        );
        if (!renamedFile) continue;

        await uploadFile(
          renamedFile,
          temporada,
          marca,
          designer,
          productName,
          "Muestras",
          muestra.ID_MUESTRA,
          muestra.MUESTRA,
        );
        existingMuestraFiles.push({ name: renamedFile.name });
      }
      loadFiles("Muestras");
    } catch (error) {
      console.error("Error al subir imagen de muestra:", error);
    }
  };

  // 7. Descargar un archivo
  const handleDownload = (file, folder) => {
    downloadFile(temporada, marca, designer, productName, folder, file.name);
  };

  // 8. Helper para truncar nombres
  const truncateFileName = (name, length = 50) => {
    return name.length <= length ? name : `${name.substring(0, length)}...`;
  };

  // 9. Obtener marcas si se abre el Drawer
  useEffect(() => {
    const fetchMarcas = async () => {
      try {
        const marcasData = await getMarcas();
        setMarcas(marcasData || []);
      } catch (error) {
        console.error("Error fetching marcas:", error);
      }
    };
    if (muestraDrawerOpen) fetchMarcas();
  }, [muestraDrawerOpen]);

  // 9. Obtener tipos de muestra para el select del Drawer
  useEffect(() => {
    const fetchTipos = async () => {
      try {
        const tipos = await getMuestrasTipo();
        setTiposMuestra(tipos);
      } catch (err) {
        console.error("Error loading tipos muestra:", err);
      }
    };

    fetchTipos();
  }, []);

  const parseDateOnly = parseMuestraDate;

  // 10. Abrir Drawer para editar muestra
  const handleOpenMuestraDrawer = async (muestra) => {
    try {
      const muestraActualizada = await getMuestraById(muestra.ID_MUESTRA);
      const escandalloData = await getEscandallosByMuestraId(
        muestra.ID_MUESTRA,
      );

      const escandallo =
        escandalloData.length > 0
          ? escandalloData[0]
          : {
              Proveedor: "",
              ESCANDALLO_GRUPOS: "",
              ESCANDALLO_SUBGRUPO: "",
              Un: "",
              Coste: "",
            };

      const subgruposData = escandallo.ESCANDALLO_GRUPOS
        ? await getEscandalloSubgruposMuestras(escandallo.ESCANDALLO_GRUPOS)
        : [];

      setSelectedMuestra({
        ...muestraActualizada,
        PROVEEDOR_ESCANDALLO: escandallo.Proveedor || "",
        ESCANDALLO_GRUPOS: escandallo.ESCANDALLO_GRUPOS || "",
        ESCANDALLO_SUBGRUPO: escandallo.ESCANDALLO_SUBGRUPO || "",
        UN: escandallo.Un || "",
        COSTE: escandallo.Coste || "",
        FECHA_PETICION: parseDateOnly(muestraActualizada.FECHA_PETICION),
        FECHA_PREVISTA: parseDateOnly(muestraActualizada.FECHA_PREVISTA),
        FECHA_GET_SUPPLIER: parseDateOnly(
          muestraActualizada.FECHA_GET_SUPPLIER,
        ),
        FECHA_PREVISTA_FABRICA: parseDateOnly(
          muestraActualizada.FECHA_PREVISTA_FABRICA,
        ),
        FECHA_ENVIO: parseDateOnly(muestraActualizada.FECHA_ENVIO),
        FECHA_RECEPCION: parseDateOnly(muestraActualizada.FECHA_RECEPCION),
      });
      setEscandalloSubgrupos(subgruposData);
      setMuestraDrawerOpen(true);
    } catch (error) {
      console.error("Error al abrir el drawer de muestra:", error);
    }
  };

  // 11. Guardar cambios en la muestra
  const handleSaveMuestraChanges = async () => {
    if (!selectedMuestra) return;
    try {
      await updateMuestra(selectedMuestra.ID_MUESTRA, {
        FECHA_PETICION: selectedMuestra.FECHA_PETICION,
        FECHA_PREVISTA: selectedMuestra.FECHA_PREVISTA,
        FECHA_RECEPCION: selectedMuestra.FECHA_RECEPCION,
        FECHA_ENVIO: selectedMuestra.FECHA_ENVIO,
        FECHA_GET_SUPPLIER: selectedMuestra.FECHA_GET_SUPPLIER,
        NOTAS: selectedMuestra.NOTAS,
        PROVEEDOR: selectedMuestra.PROVEEDOR,
        ID_ESTADO: selectedMuestra.ID_ESTADO,
        ID_TALLA_BASE: selectedMuestra.ID_TALLA_BASE,
        ID_COLOR_BASE: selectedMuestra.ID_COLOR_BASE,
      });
      await onRefresh?.();
      setMuestraDrawerOpen(false);
    } catch (error) {
      console.error("Error al guardar los cambios en la muestra:", error);
    }
  };

  // 12. Eliminar un archivo subido
  const handleDeleteFile = async (file, folder) => {
    if (!temporada || !marca || !designer || !productName || !folder || !file) {
      console.warn("Datos incompletos para eliminar el archivo:", {
        temporada,
        marca,
        designer,
        productName,
        folder,
        file,
      });
      return;
    }

    try {
      await deleteSpecificFile(
        temporada,
        marca,
        designer,
        productName,
        folder,
        file.name,
      );
      // Recargar archivos después de eliminar
      loadFiles(folder);
    } catch (error) {
      console.error("Error al eliminar el archivo:", error);
    }
  };

  // efecto que recalcula si hay archivos cada vez que cambie files o productId
  useEffect(() => {
    if (!onFilesStatusChange || !productId) return;

    const hasMuestraFiles = (files.Muestras || []).length > 0;

    onFilesStatusChange(productId, hasTechnicalFiles || hasMuestraFiles);
  }, [files, productId, onFilesStatusChange, hasTechnicalFiles]);

  /* DIALOGO CONFIRMACION BORRADO */
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);

  const handleOpenDeleteDialog = (file, folder) => {
    setFileToDelete(file);
    setFolderToDelete(folder);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setFileToDelete(null);
    setFolderToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (fileToDelete && folderToDelete) {
      await handleDeleteFile(fileToDelete, folderToDelete);
    }
    handleCloseDeleteDialog();
  };

  // 13. Render Final
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        alignSelf: "stretch",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* CABECERA — misma altura/margen que el buscador del tree */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, sm: 2 },
          py: 1,
          mb: 2,
          minHeight: 72,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(0,188,212,0.12) 0%, rgba(103,58,183,0.1) 100%)",
          boxSizing: "border-box",
        }}
      >
        <Typography
          variant="overline"
          sx={{
            opacity: 0.75,
            letterSpacing: 1,
            display: "block",
            lineHeight: 1.2,
          }}
        >
          Diseño
        </Typography>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          noWrap
          title={productName || "Producto seleccionado"}
          sx={{
            fontWeight: 800,
            lineHeight: 1.15,
            color: "text.primary",
          }}
        >
          {productName || "Producto seleccionado"}
        </Typography>
      </Box>
      {/* MUESTRAS */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
            {errorMessage}
          </Alert>
        )}
        <TechnicalAttachmentsPanel
          productId={productId}
          productName={productName}
          temporada={temporada}
          marca={marca}
          designer={designer}
          clientName={firstClientName}
          muestras={muestras || []}
          selectedMuestraId={selectedSampleId}
          onSelectMuestra={(id) => setSelectedSampleId(id)}
          onFilesChange={setHasTechnicalFiles}
          refreshKey={techRefreshKey}
          renderSampleColumn={(muestra) => {
            const isSelected =
              Number(selectedSampleId) === Number(muestra.ID_MUESTRA);
            const isTechDropTarget =
              dragOverTechTargetId === muestra.ID_MUESTRA;
            const isPhotoDropTarget =
              dragOverFolder === `Muestras-${muestra.ID_MUESTRA}`;

            return (
              <Box
                onClick={() => setSelectedSampleId(muestra.ID_MUESTRA)}
                sx={{
                  ...pastillaBaseSx,
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: isTechDropTarget
                    ? "primary.main"
                    : isSelected
                      ? "primary.main"
                      : "divider",
                  borderStyle: isTechDropTarget ? "dashed" : "solid",
                  backgroundColor: isTechDropTarget
                    ? alpha(theme.palette.primary.main, 0.12)
                    : isSelected
                      ? alpha(theme.palette.primary.main, 0.08)
                      : isPhotoDropTarget
                        ? alpha(theme.palette.primary.main, 0.06)
                        : alpha(theme.palette.background.paper, 0.9),
                  boxShadow: isSelected
                    ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`
                    : "none",
                  transition: "all 0.2s ease-in-out",
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const types = Array.from(e.dataTransfer?.types || []);
                  const isFileDrop = types.includes("Files");
                  const isTechMove =
                    !isFileDrop &&
                    (types.includes(TECH_DRAG_MIME) ||
                      types.includes("text/plain"));
                  if (isTechMove) {
                    setDragOverTechTargetId(muestra.ID_MUESTRA);
                    e.dataTransfer.dropEffect = "move";
                  } else {
                    setDragOverFolder(`Muestras-${muestra.ID_MUESTRA}`);
                  }
                }}
                onDragLeave={handleDragLeave}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverFolder(null);
                  setDragOverTechTargetId(null);
                  const moved = await handleMoveTechnicalToSample(
                    muestra,
                    e.dataTransfer,
                  );
                  if (moved) return;
                  const droppedFiles = Array.from(e.dataTransfer.files || []);
                  if (droppedFiles.length) {
                    uploadMuestraImage(droppedFiles, muestra);
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: ".95em",
                        color: "text.primary",
                      }}
                    >
                      {muestra.MUESTRA}
                    </Typography>
                    {muestra.NombreCliente && (
                      <Typography
                        sx={{
                          fontSize: ".75em",
                          fontWeight: 500,
                          color: "text.secondary",
                          mt: 0.15,
                        }}
                      >
                        {muestra.NombreCliente}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    {muestra.FECHA_PREVISTA && (
                      <Typography
                        sx={{
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          padding: "0.15em 0.45em",
                          borderRadius: 1,
                          color: "primary.main",
                          fontSize: ".7em",
                        }}
                      >
                        {t("Samples.FECHA_PREVISTA")}:
                        {dayjs(muestra.FECHA_PREVISTA).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    {muestra.FECHA_PETICION && (
                      <Typography
                        sx={{
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          padding: "0.15em 0.45em",
                          borderRadius: 1,
                          color: "secondary.main",
                          fontSize: ".7em",
                        }}
                      >
                        {t("Samples.FECHA_PETICION")}:
                        {dayjs(muestra.FECHA_PETICION).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    {muestra.FECHA_GET_SUPPLIER && (
                      <Typography
                        sx={{
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          padding: "0.15em 0.45em",
                          borderRadius: 1,
                          color: "warning.main",
                          fontSize: ".7em",
                        }}
                      >
                        {t("Samples.FECHA_GET_SUPPLIER")}:
                        {dayjs(muestra.FECHA_GET_SUPPLIER).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    {muestra.FECHA_ENVIO && (
                      <Typography
                        sx={{
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          padding: "0.15em 0.45em",
                          borderRadius: 1,
                          color: "error.main",
                          fontSize: ".7em",
                        }}
                      >
                        {t("Samples.FECHA_ENVIO")}:
                        {dayjs(muestra.FECHA_ENVIO).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    {muestra.FECHA_RECEPCION && (
                      <Typography
                        sx={{
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          padding: "0.15em 0.45em",
                          borderRadius: 1,
                          color: "success.main",
                          fontSize: ".7em",
                        }}
                      >
                        {t("Samples.FECHA_RECEPCION")}:
                        {dayjs(muestra.FECHA_RECEPCION).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    mt: 1,
                    mb: 0.75,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {userCanMoodboard ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<DashboardCustomizeOutlinedIcon />}
                      onClick={() => setBoardMuestraId(muestra.ID_MUESTRA)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        py: 0.25,
                        px: 1,
                        borderRadius: 1.5,
                        boxShadow: "none",
                        minHeight: 28,
                      }}
                    >
                      Tablero
                    </Button>
                  ) : null}
                  <Tooltip title="Ver muestra">
                    <IconButton
                      size="small"
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: "2px",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          borderColor: "primary.main",
                        },
                      }}
                      onClick={() => handleOpenMuestraDrawer(muestra)}
                    >
                      <RemoveRedEyeOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id={`upload-muestra-${muestra.ID_MUESTRA}`}
                    type="file"
                    multiple
                    onChange={(e) => {
                      const selectedFiles = Array.from(e.target.files || []);
                      if (!selectedFiles.length) return;
                      uploadMuestraImage(selectedFiles, muestra);
                    }}
                  />
                  <label htmlFor={`upload-muestra-${muestra.ID_MUESTRA}`}>
                    <Tooltip title="Subir archivo">
                      <IconButton
                        component="span"
                        size="small"
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: "2px",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <UploadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </label>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    flexWrap: "wrap",
                    mt: "auto",
                  }}
                >
                  {getMuestraFiles(muestra).map((file, i) => (
                    <Box
                      key={`${file.name}-${i}`}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.35,
                        width: 72,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: alpha(theme.palette.background.paper, 0.9),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "& img, & canvas, & > *": {
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "cover",
                          },
                        }}
                      >
                        <FileThumbnails filePath={file.path} />
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              p: "2px",
                            }}
                            onClick={() => handleDownload(file, "Muestras")}
                          >
                            <DownloadIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            sx={{
                              border: "1px solid",
                              borderColor: alpha(theme.palette.error.main, 0.5),
                              color: "error.main",
                              borderRadius: 1,
                              p: "2px",
                            }}
                            onClick={() =>
                              handleOpenDeleteDialog(file, "Muestras")
                            }
                          >
                            <DeleteOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          }}
        />

        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Confirm Deletion?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete the file{" "}
              <strong>{fileToDelete?.name}</strong>?<br /> This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              autoFocus
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Drawer para editar muestra */}
      {/* <DrawerMuestra */}
      {selectedMuestra && (
        <DrawerMuestraV2
          open={muestraDrawerOpen}
          onClose={handleCloseDrawer}
          newMuestra={selectedMuestra}
          setNewMuestra={setSelectedMuestra}
          tiposMuestra={tiposMuestra}
          tiposEstado={tiposEstado}
          tiposTallas={tiposTallas}
          tiposColores={tiposColores}
          tiposProveedores={tiposProveedores}
          tiposMarcas={marcas}
          escandalloGrupos={escandalloGrupos}
          handleGrupoChange={handleGrupoChange}
          selectedProduct={{
            id: productId,
            productName,
            temporada,
            marca,
            designer,
          }}
          isFromTree={false}
          isEditMode={true}
          userCanCreateSample={userCanCreateSample}
          onSaveMuestra={(id, savedMuestra) => {
            if (onMuestraUpdated) {
              onMuestraUpdated(savedMuestra);
            }
          }}
          onDeleteMuestra={(id) => {
            if (onDeleteMuestra) {
              onDeleteMuestra(id);
            }
            loadFiles("Muestras");
            handleCloseDrawer();
          }}
        />
      )}
      {boardMuestraId ? (
        <Suspense fallback={null}>
          <SampleBoardDialog
            open
            muestraId={boardMuestraId}
            onClose={() => {
              setBoardMuestraId(null);
              setTechRefreshKey((k) => k + 1);
            }}
          />
        </Suspense>
      ) : null}
    </Box>
  );
};

export default FileUploader;
